import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import React, { useRef, useState, useEffect, useCallback } from "react";
import Layout from '../../Layout';
import CreateCustomerModal from "../../components/Customer/CreateCustomerModal";
import CreateTaskModal from "../../components/Customer/CreateTaskModal";
import WorkHistoryModal from "../../components/Customer/WorkHistoryModal";
import CustomerDetailDrawer from "./components/CustomerDetailDrawer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseFilters, buildFilterParams, saveCustomerListQuery, CustomerListFilters } from "./filterState";
import { getChannelCustomers, ChannelCustomerItem, deleteCustomerApi, updateCustomer, CustomerSpam, getCustomerDetail } from "../../api/ChannelCustomer";
import { getNextTags } from "../../utils/appointmentNextTags";
import { extractApiError } from "../../lib/apiError";
import { isEditableFallbackTagName } from "../../lib/customerTag";
import useAuthStore from "../../store/authStore";
import { mixGetV1 } from "../../services/mixin";
import { useDebounce } from "../../hooks/useDebounce";
import { ShieldAlert, ImageIcon } from "lucide-react";
import {
    Table,
    Button,
    Tag,
    Avatar,
    Modal,
    Dropdown,
    message,
    Upload,
    Select,
    Tooltip,
} from "antd";
import {
    PlusOutlined,
    SyncOutlined,
    CheckOutlined,
    LoadingOutlined,
    UserOutlined,
    MoreOutlined,
    ShoppingCartOutlined,
    MessageOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    StarFilled,
    FileTextOutlined,
    EditOutlined,
    PhoneOutlined
} from "@ant-design/icons";
import type { MenuProps, UploadProps } from "antd";
import { Image } from "antd";
import Toolbar from "components/Customer/Toolbar";
import StatusTabs from "components/Customer/StatusTabs";
import useTableColumnSettings, { SettingColumn } from "hooks/useTableColumnSettings";
import { ColumnSettingButton } from "components/TableColumnSetting";
import dayjs from "dayjs";

/* ================= TYPES ================= */

interface CustomerItem {
    key: string;
    code: string;
    name: string;
    phone: string;
    serviceCategory: "Newhair" | "Clinic" | "PTTM" | "Partnership";
    csCode: "CS0" | "CS1" | "CS2" | "CS3" | "CS4" | "CS5" | "CS6";
    flags: string[];
    latestService: string;
    date: string;
    remainingSessions: string;
    nextMilestone: string;
}

const SELECTION_COL_WIDTH = 48;

const CHANNEL_META: Record<string, { label: string; color: string }> = {
    fb: { label: "Facebook", color: "#1877F2" },
    facebook: { label: "Facebook", color: "#1877F2" },
    insta: { label: "Instagram", color: "#E1306C" },
    instagram: { label: "Instagram", color: "#E1306C" },
    zalo: { label: "Zalo", color: "#0068FF" },
    tiktok: { label: "TikTok", color: "#111111" },
};

const SOURCE_LABELS: Record<string, string> = {
    facebook: "Facebook",
    fb: "Facebook",
    zalo: "Zalo",
    tiktok: "TikTok",
    insta: "Instagram",
    instagram: "Instagram",
    manual: "Nhập tay",
};

const EmptyCell = () => <span style={{ color: "#aaa" }}>—</span>;

const formatDateTime = (value?: string | null, lang = "vi") => {
    if (!value) return null;
    const d = dayjs(value);
    if (!d.isValid()) return null;
    return d.format(lang === "vi" ? "DD/MM/YYYY HH:mm" : "MM/DD/YYYY HH:mm");
};

const CustomerCare: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();

    const [searchParams, setSearchParams] = useSearchParams();
    const initialFiltersRef = useRef<CustomerListFilters | null>(null);
    if (initialFiltersRef.current === null) {
        initialFiltersRef.current = parseFilters(searchParams);
    }
    const initialFilters = initialFiltersRef.current;

    const [customerCareStatusId, setCustomerCareStatusId] = useState<number | null>(initialFilters.tagId);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [openTask, setOpenTask] = useState(false);
    const [openHistory, setOpenHistory] = useState(false);
    const [historyCustomerId, setHistoryCustomerId] = useState<number | null>(null);
    const navigate = useNavigate();
    const [deleteCustomer, setDeleteCustomer] = useState<ChannelCustomerItem | null>(null);

    const [selectedCustomer, setSelectedCustomer] = useState<CustomerItem | null>(null);
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    const [openSpamTooltipId, setOpenSpamTooltipId] = useState<number | null>(null);
    const [openSpamModal, setOpenSpamModal] = useState(false);
    const [selectSpam, setSelectSpam] = useState<CustomerSpam | null>(null);

    const canEditCustomerTag = isSuperAdmin || hasPermission("customer.edit");
    const [tagOptions, setTagOptions] = useState<{ value: number; label: string; color?: string }[]>([]);
    const [updatingTagRowId, setUpdatingTagRowId] = useState<number | null>(null);

    useEffect(() => {
        if (!canEditCustomerTag) return;
        mixGetV1('/api/v1/be/customer-tags', { limit: 100, sort: ["id,desc"], group: 'group_1' })
            .then((res) => {
                if (res?.status && res.data?.items) {
                    setTagOptions(res.data.items.map((tag: any) => ({
                        value: tag.id,
                        label: tag.name,
                        color: tag.color || "#1890ff",
                    })));
                }
            })
            .catch(() => {});
    }, [canEditCustomerTag]);

    const handleChangeCustomerTag = async (record: ChannelCustomerItem, tagId: number) => {
        if (record.customer_tag?.id === tagId) return;
        setUpdatingTagRowId(record.id);
        const res = await updateCustomer(record.id, {
            display_name: record.display_name,
            customer_tag_id: tagId,
        });
        setUpdatingTagRowId(null);
        if (!res.status) {
            message.error(extractApiError(res, i18n.language) || t("roomchat.update_failed") || "Cập nhật thất bại");
            return;
        }
        if (Array.isArray(record.dynamic_next_tags)) {
            const detail = await getCustomerDetail(record.id);
            const next = getNextTags(record).find((o) => o.id === tagId);
            updateRow(record.id, {
                customer_tag: detail.data?.customer_tag
                    ?? (next ? { id: next.id, name: next.name, color: next.color } : record.customer_tag),
                dynamic_next_tags: detail.data ? getNextTags(detail.data) : [],
            });
        } else {
            const opt = tagOptions.find((o) => o.value === tagId);
            updateRow(record.id, {
                customer_tag: opt ? { id: opt.value, name: opt.label, color: opt.color } : record.customer_tag,
            });
        }
        setStatusRefreshKey((k) => k + 1);
        message.success(t("roomchat.update_success") || "Cập nhật thành công");
    };

    const allColumns: SettingColumn<ChannelCustomerItem>[] = [
        {
            key: "customerName",
            title: "KHÁCH HÀNG",
            render: (_, record) => {
                // Phục hồi dữ liệu đầy đủ để Drawer không bị teo nhỏ
                const customerItemObj: CustomerItem = {
                    key: String(record.id),
                    code: record.customer_code || `KH-${record.id}`,
                    name: record.display_name || record.fullname || "Khách hàng",
                    phone: record.phone || "",
                    serviceCategory: "Newhair",
                    csCode: "CS2",
                    flags: ["Đang theo dõi sau dịch vụ"],
                    latestService: record.note || "Phác đồ chăm sóc khách hàng",
                    date: dayjs(record.created_at).format("DD/MM") || "14/10",
                    remainingSessions: "2 buổi",
                    nextMilestone: "T+24h hỏi thăm",
                };

                return (
                    <span 
                        className="font-medium text-[#3e91ab] cursor-pointer hover:underline" 
                        onClick={() => setSelectedCustomer(customerItemObj)}
                    >
                        {record.customer_code || `KH-${record.id}`} · {record.display_name}
                    </span>
                );
            },
        },
        {
            key: "phone",
            title: "SĐT",
            render: (_, record) => {
                const customerItemObj: CustomerItem = {
                    key: String(record.id),
                    code: record.customer_code || `KH-${record.id}`,
                    name: record.display_name || record.fullname || "Khách hàng",
                    phone: record.phone || "",
                    serviceCategory: "Newhair",
                    csCode: "CS2",
                    flags: ["Đang theo dõi sau dịch vụ"],
                    latestService: record.note || "Phác đồ chăm sóc khách hàng",
                    date: dayjs(record.created_at).format("DD/MM") || "14/10",
                    remainingSessions: "2 buổi",
                    nextMilestone: "T+24h hỏi thăm",
                };

                const maskPhone = (phone?: string) => {
                    if (!phone || phone.length < 8) return phone;
                    return phone.substring(0, 3) + '***' + phone.substring(phone.length - 4);
                };

                return (
                    <span 
                        className="text-[#3e91ab] cursor-pointer hover:underline underline-offset-2" 
                        onClick={() => setSelectedCustomer(customerItemObj)}
                    >
                        {maskPhone(record.phone)}
                    </span>
                );
            },
        },
        {
            key: "serviceCategory",
            title: "MẢNG",
            render: () => <span className="text-gray-700">Newhair</span>,
        },
        {
            key: "csCode",
            title: "MÃ S",
            render: () => (
                <Tag className="bg-[#486e92] text-white font-bold border-0 rounded-full px-2.5 m-0">S4</Tag>
            ),
        },
        {
            key: "deadline",
            title: "HẠN / VIỆC TIẾP",
            render: () => <span className="text-red-600 font-bold">Quá 18 phút</span>,
        },
        {
            key: "source",
            title: "NGUỒN",
            render: () => <span className="text-gray-700">Facebook</span>,
        },
        {
            key: "staff",
            title: "PHỤ TRÁCH",
            render: (_, record) => <span className="text-gray-700">{record.in_charge_staff?.name || record.staff?.name || "NV-03"}</span>,
        },
        {
            key: "createdAt",
            title: "NGÀY TẠO",
            render: (_, record) => <span className="text-gray-700">{dayjs(record.created_at).format("DD/MM")}</span>,
        },
        {
            key: "lastTouch",
            title: "LẦN CHẠM CUỐI",
            render: () => <span className="text-gray-700">14/10 · KN-04</span>,
        },
        {
            key: "branch",
            title: "CHI NHÁNH",
            render: () => <span className="text-gray-700">Trần Hưng Đạo Q1</span>,
        },
        {
            key: "actions",
            title: "",
            align: "center",
            width: 140,
            fixed: "right",
            render: () => {
                return (
                    <div className="flex items-center gap-1.5 justify-center">
                        <Button size="small" type="primary" className="!bg-[#3E91AB] text-xs h-7 px-2.5 flex items-center gap-1">
                            <PhoneOutlined /> Gọi
                        </Button>
                        <Button size="small" className="text-xs h-7 px-2.5 border-gray-300">Zalo</Button>
                    </div>
                );
            },
        }
    ];

    // Lọc chỉ giữ lại 6 cột quan trọng khi Ngăn hồ sơ bên phải được mở ra
    const columns = selectedCustomer 
        ? allColumns.filter(c => ["customerName", "phone", "serviceCategory", "csCode", "deadline", "actions"].includes(c.key as string))
        : allColumns;

    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings("customer_list", columns, { extraWidth: SELECTION_COL_WIDTH });

    const rowSelection = {
        columnWidth: SELECTION_COL_WIDTH,
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    };

    const [data, setData] = useState<ChannelCustomerItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState(initialFilters.search);
    const [page, setPage] = useState(initialFilters.page);
    const [total, setTotal] = useState(0);
    const [staffId, setStaffId] = useState<number | undefined>(initialFilters.staffId);
    const [pageStaffId, setPageStaffId] = useState<number | undefined>(initialFilters.pageStaffId);
    const [startDate, setStartDate] = useState<string | undefined>(initialFilters.startDate);
    const [endDate, setEndDate] = useState<string | undefined>(initialFilters.endDate);
    const [oauthAccountIds, setOauthAccountIds] = useState<number[]>(initialFilters.oauthAccountIds);
    const [channelConnectionIds, setChannelConnectionIds] = useState<number[]>(initialFilters.channelConnectionIds);
    const [careStatusIds, setCareStatusIds] = useState<number[]>(initialFilters.careStatusIds);
    const [period, setPeriod] = useState<string>(initialFilters.period);
    const [refDate, setRefDate] = useState<string | undefined>(initialFilters.refDate);
    const debouncedSearch = useDebounce(searchText, 400);

    useEffect(() => {
        const params = buildFilterParams({
            search: debouncedSearch,
            tagId: customerCareStatusId,
            page,
            staffId,
            pageStaffId,
            startDate,
            endDate,
            period,
            refDate,
            oauthAccountIds,
            channelConnectionIds,
            careStatusIds,
        });
        const qs = params.toString();
        saveCustomerListQuery(qs);
        setSearchParams(params, { replace: true });
    }, [
        setSearchParams,
        debouncedSearch,
        customerCareStatusId,
        page,
        staffId,
        pageStaffId,
        startDate,
        endDate,
        period,
        refDate,
        oauthAccountIds,
        channelConnectionIds,
        careStatusIds,
    ]);

    const fetchCustomers = useCallback(async (
        search?: string,
        statusId?: number | null,
        currentPage = 1,
        staff?: number,
        pageStaff?: number,
        start?: string,
        end?: string,
        oauthIds?: number[],
        connectionIds?: number[],
        careIds?: number[],
    ) => {
        setLoading(true);
        const res = await mixGetV1('/api/v1/be/customers', {
            search: search || undefined,
            customer_tag_id: statusId || undefined,
            contact_staff_id: staff || undefined,
            in_charge_staff_id: pageStaff || undefined,
            start_date: start || undefined,
            end_date: end || undefined,
            oauth_account_id: oauthIds && oauthIds.length ? oauthIds : undefined,
            channel_connection_id: connectionIds && connectionIds.length ? connectionIds : undefined,
            customer_care_status_ids: careIds && careIds.length ? careIds : undefined,
            page: currentPage,
        });
        if (res.status && res.data) {
            setData(res.data.items);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchCustomers(debouncedSearch, customerCareStatusId, page, staffId, pageStaffId, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds);
    }, [fetchCustomers, debouncedSearch, customerCareStatusId, page, staffId, pageStaffId, startDate, endDate, oauthAccountIds, channelConnectionIds, careStatusIds]);

    const handleSearch = useCallback((value: string) => {
        setPage(1);
        setSearchText(value);
    }, []);

    const handleStaffChange = useCallback((value?: number) => {
        setPage(1);
        setStaffId(value);
    }, []);

    const handlePageStaffChange = useCallback((value?: number) => {
        setPage(1);
        setPageStaffId(value);
    }, []);

    const handleDateChange = useCallback((start?: string, end?: string, nextPeriod?: string, nextRefDate?: string) => {
        setPage(1);
        setStartDate(start);
        setEndDate(end);
        if (nextPeriod) setPeriod(nextPeriod);
        setRefDate(nextRefDate);
    }, []);

    const handleSourceChange = useCallback((oauthIds: number[], connectionIds: number[]) => {
        setPage(1);
        setOauthAccountIds(oauthIds);
        setChannelConnectionIds(connectionIds);
    }, []);

    const handleCareStatusChange = useCallback((ids: number[]) => {
        setPage(1);
        setCareStatusIds(ids);
    }, []);

    const [deleteLoading, setDeleteLoading] = useState(false);
    const [statusRefreshKey, setStatusRefreshKey] = useState(0);

    const updateRow = (id: number, newData: Partial<ChannelCustomerItem>) => {
        setData((prev) =>
            prev.map((item) => (item.id === id ? { ...item, ...newData } : item))
        );
    };

    const handleDeleteCustomer = async () => {
        const customerId = deleteCustomer?.customer?.id ?? deleteCustomer?.id;
        if (!customerId) return;
        setDeleteLoading(true);
        const res = await deleteCustomerApi(customerId);
        setDeleteLoading(false);
        if (res.status) {
            message.success(t('customer.delete_success'));
            setDeleteCustomer(null);
            fetchCustomers(searchText, customerCareStatusId, page);
            setStatusRefreshKey((k) => k + 1);
        } else {
            message.error(extractApiError(res, i18n.language) || t('customer.delete_failed'));
        }
    };

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available": return "#52c41a";
            case "break": return "#d48806";
            case "offline": return "#8c8c8c";
            default: return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>{t('page.customers')} | Khothi</title>
            </Helmet>
            
            <div className="min-h-screen text-sm flex flex-col justify-between transition-colors duration-200">
                <div className="flex flex-1 gap-4 w-full">
                    <div className="space-y-4 pb-16 flex-1 min-w-0">
                        
                        <Toolbar
                            onCreate={() => setOpenCreate(true)}
                            onSearch={handleSearch}
                            onStaffChange={handleStaffChange}
                            onPageStaffChange={handlePageStaffChange}
                            onDateChange={handleDateChange}
                            onSourceChange={handleSourceChange}
                            onCareStatusChange={handleCareStatusChange}
                            initialFilters={initialFilters}
                        />

                        <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                                <StatusTabs
                                    activeTagId={customerCareStatusId}
                                    onFilterChange={(statusId) => setCustomerCareStatusId(statusId)}
                                    refreshKey={statusRefreshKey}
                                    contactStaffId={staffId}
                                    inChargeStaffId={pageStaffId}
                                    search={debouncedSearch}
                                    startDate={startDate}
                                    endDate={endDate}
                                    oauthAccountIds={oauthAccountIds}
                                    channelConnectionIds={channelConnectionIds}
                                    careStatusIds={careStatusIds}
                                />
                            </div>
                            <ColumnSettingButton {...columnSettingProps} />
                        </div>

                        <Table
                            rowKey="id"
                            loading={loading}
                            columns={tableColumns}
                            components={tableComponents}
                            tableLayout={tableLayout}
                            dataSource={data}
                            pagination={{
                                current: page,
                                pageSize: 20,
                                total: total,
                                showSizeChanger: false,
                                onChange: (p) => setPage(p),
                            }}
                            scroll={{ x: scrollX }}
                            className="custom-ant-table"
                        />

                        <Modal
                            open={!!deleteCustomer}
                            onCancel={() => setDeleteCustomer(null)}
                            footer={null}
                            width={480}
                            className="
                                dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                            "
                        >
                            <div className="flex gap-4">
                                <ExclamationCircleFilled style={{ fontSize: 24, color: "#faad14" }} />
                                <div>
                                    <div className="text-lg font-semibold mb-2 t-text-white">
                                        {t('customer.delete_customer')}
                                    </div>
                                    <div className="text-gray-600 mb-6">
                                        {t('customer.confirm_delete')} "{deleteCustomer?.display_name}"?
                                    </div>
                                    <div className="flex justify-end gap-3">
                                        <Button onClick={() => setDeleteCustomer(null)}>
                                            {t('customer.cancel')}
                                        </Button>
                                        <Button danger type="primary" loading={deleteLoading} onClick={handleDeleteCustomer}>
                                            {t('customer.delete')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Modal>

                        <CreateCustomerModal
                            open={openCreate}
                            onClose={() => setOpenCreate(false)}
                            onSubmit={(values) => {
                                fetchCustomers(searchText, customerCareStatusId, page);
                                setStatusRefreshKey((k) => k + 1);
                            }}
                        />
                        <CreateTaskModal
                            open={openTask}
                            onClose={() => setOpenTask(false)}
                            onSubmit={(values) => console.log(values)}
                        />
                        <WorkHistoryModal
                            open={openHistory}
                            onClose={() => { setOpenHistory(false); setHistoryCustomerId(null); }}
                            customerId={historyCustomerId}
                        />

                        <Modal
                            open={openSpamModal}
                            footer={null}
                            onCancel={() => setOpenSpamModal(false)}
                            width={1000}
                            title={<div className="text-[20px] font-bold">Hình ảnh bằng chứng Spam</div>}
                            className="
                                dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                                dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                                dark:[&_.ant-modal-title]:text-white
                            "
                        >
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                {selectSpam?.image_1 && (
                                    <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
                                        <Image src={selectSpam.image_1} className="h-full w-full object-cover" />
                                    </div>
                                )}
                                {selectSpam?.image_2 && (
                                    <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
                                        <Image src={selectSpam.image_2} className="h-full w-full object-cover" />
                                    </div>
                                )}
                                {selectSpam?.image_3 && (
                                    <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
                                        <Image src={selectSpam.image_3} className="h-full w-full object-cover" />
                                    </div>
                                )}
                            </div>
                        </Modal>
                    </div>

                    {selectedCustomer && (
                        <CustomerDetailDrawer
                            customer={selectedCustomer}
                            onClose={() => setSelectedCustomer(null)}
                        />
                    )}
                </div>

                <div className="sticky bottom-3 z-27 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getStatusDotColor() }}></span>
                            <span className="font-bold text-sm whitespace-nowrap">NV-07 · CSKH</span>
                        </div>
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">Không có cuộc gọi</span>
                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button size="small" className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial">
                                Mô phỏng gọi đến
                            </Button>
                            <Button size="small" className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial">
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CustomerCare;